import { Component, OnInit, OnDestroy, EventEmitter, Output, ViewChild, ElementRef } from '@angular/core';
import { trigger, state, style, transition, animate, query, stagger } from '@angular/animations';
import { Subject, of } from 'rxjs';
import { takeUntil, take, catchError } from 'rxjs/operators';
import { CustomerService } from '../../services/customer.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';

type PendingAuthsSortCol = 'orderno' | 'customer' | 'amount' | 'created_iso' | 'expires';

@Component({
	selector: 'app-pending-auths',
	templateUrl: './pending-auths.component.html',
	styleUrls: ['./pending-auths.component.scss'],
	animations: [
		trigger('flipState', [
			state('active', style({ transform: 'rotate3d(0, 1, 0, 179deg)' })),
			state('inactive', style({ transform: 'rotateY(0)' })),
			transition('active => inactive', animate('500ms ease-out')),
			transition('inactive => active', animate('500ms ease-in')),
		]),
		trigger('grow', [
			transition(':enter', [
				style({ height: '0', width: '0' }),
				animate(200, style({ height: '*', width: '*' })),
				animate('200ms', style({ opacity: 1 })),
			]),
			transition(':leave', [
				animate(100, style({ height: 0, width: 0 })),
				animate('100ms', style({ opacity: 0 })),
			]),
		]),
	]
})
export class PendingAuthsComponent implements OnInit, OnDestroy {
	@Output() viewData = new EventEmitter<any>();
	@ViewChild('print_table') printTable: ElementRef<HTMLElement>;

	flip = 'inactive';
	preauthEnabled = false;
	items: any[] = [];
	expiringSoonCount = 0;
	isLoading = true;
	title = 'Pending Auths';
	config: any = null;
	actionBusy: Record<string, boolean> = {};
	voidFeedback: { type: 'success' | 'error'; text: string } | null = null;

	searchTerm = '';
	expiryFilter: 'all' | 'soon' = 'all';
	sortColumn: PendingAuthsSortCol = 'created_iso';
	sortDir: 'asc' | 'desc' = 'desc';
	exporting = false;

	private readonly destroy$ = new Subject<void>();
	private voidFeedbackClearTimer: ReturnType<typeof setTimeout> | null = null;

	constructor(
		private customerService: CustomerService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService
	) {}

	get displayData(): any[] {
		let rows = [...(this.items || [])];
		if (this.expiryFilter === 'soon') {
			rows = rows.filter((r: any) => r.expiring_soon);
		}
		const term = (this.searchTerm || '').trim().toLowerCase();
		if (term) {
			rows = rows.filter((r: any) => {
				const blob = [
					r.orderno,
					r.debtorno,
					r.customer_name,
					r.amount,
					r.created_iso,
					r.days_until_expiry,
					r.expiring_soon ? 'expiring soon' : '',
				]
					.map((x) => (x ?? '').toString().toLowerCase())
					.join(' ');
				return blob.includes(term);
			});
		}
		const col = this.sortColumn;
		const dir = this.sortDir === 'asc' ? 1 : -1;
		rows.sort((a: any, b: any) => {
			let cmp = 0;
			switch (col) {
				case 'orderno':
					cmp = String(a.orderno || '').localeCompare(String(b.orderno || ''), undefined, { numeric: true });
					break;
				case 'customer':
					cmp = String(a.customer_name || a.debtorno || '').localeCompare(
						String(b.customer_name || b.debtorno || ''),
						undefined,
						{ sensitivity: 'base' }
					);
					break;
				case 'amount':
					cmp = (Number(a.amount) || 0) - (Number(b.amount) || 0);
					break;
				case 'created_iso':
					cmp = String(a.created_iso || '').localeCompare(String(b.created_iso || ''));
					break;
				case 'expires':
					cmp = (Number(a.days_until_expiry) || 0) - (Number(b.days_until_expiry) || 0);
					break;
				default:
					cmp = 0;
			}
			return cmp * dir;
		});
		return rows;
	}

	toggleSort(col: PendingAuthsSortCol): void {
		if (this.sortColumn === col) {
			this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortColumn = col;
			if (col === 'created_iso') {
				this.sortDir = 'desc';
			} else {
				this.sortDir = 'asc';
			}
		}
	}

	sortIcon(col: PendingAuthsSortCol): string {
		if (this.sortColumn !== col) {
			return 'unfold_more';
		}
		return this.sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward';
	}

	trackByAuth(_index: number, a: any): string {
		return this.rowKey(a);
	}

	exportXlsx(): void {
		const rows = this.displayData;
		if (!rows.length) {
			return;
		}
		const exportRows = rows.map((a: any) => ({
			Order: a.orderno ?? '',
			Customer: a.customer_name || a.debtorno || '',
			'Customer#': a.debtorno ?? '',
			Amount: a.amount != null && a.amount !== '' ? Number(a.amount) : '',
			'Auth date': a.created_iso ?? '',
			'Days until expiry': a.days_until_expiry ?? '',
			'Expiring soon': a.expiring_soon ? 'Yes' : 'No',
		}));
		this.globalSearchService.exportJsonToExcel(exportRows, 'PendingAuths_' + new Date().toISOString().slice(0, 10));
	}

	exportPdf(): void {
		if (!this.printTable?.nativeElement || this.exporting || !this.displayData.length) {
			return;
		}
		this.exporting = true;
		const encoded = this.globalSearchService.base64encode(this.printTable.nativeElement.innerHTML);
		const filename = 'PendingAuths_' + new Date().toISOString().slice(0, 10);
		this.printService
			.pdf({
				content: encoded,
				filename,
				title: this.title,
				subtitle: 'Exported ' + new Date().toLocaleString(),
			})
			.subscribe({
				next: (result: any) => {
					this.globalSearchService.downloadPdf(result.content, filename);
					this.exporting = false;
				},
				error: () => {
					this.exporting = false;
				},
			});
	}

	ngOnInit(): void {
		const cfg = this.globalSearchService.getConfig();
		this.config = cfg;

		if (!cfg?.arpreauth) {
			this.isLoading = false;
			return;
		}

		this.customerService.getPendingAuths()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (res: any) => {
					this.preauthEnabled = !!res.preauth_enabled;
					this.items = Array.isArray(res.items) ? res.items : [];
					this.expiringSoonCount = this.items.filter((i: any) => i.expiring_soon).length;
					this.isLoading = false;
				},
				error: () => {
					this.isLoading = false;
				},
			});
	}

	ngOnDestroy(): void {
		if (this.voidFeedbackClearTimer) {
			clearTimeout(this.voidFeedbackClearTimer);
			this.voidFeedbackClearTimer = null;
		}
		this.destroy$.next();
		this.destroy$.complete();
	}

	toggleFlip(open: boolean) {
		if (!open) {
			this.flip = 'inactive';
			this.viewData.emit(false);
			return;
		}
		this.flip = this.flip === 'active' ? 'inactive' : 'active';
		if (this.flip === 'active') {
			this.viewData.emit({ data: this.items, title: 'Pending Auths' });
		} else {
			this.viewData.emit(false);
		}
	}

	rowKey(a: any): string {
		return a?.auth_id || a?.transid || '';
	}

	reloadList(): void {
		if (!this.config?.arpreauth) {
			return;
		}
		this.isLoading = true;
		this.customerService
			.getPendingAuths()
			.pipe(
				take(1),
				catchError(() => of({ preauth_enabled: true, items: [] as any[] }))
			)
			.subscribe((res: any) => {
				this.preauthEnabled = !!res.preauth_enabled;
				this.items = Array.isArray(res.items) ? res.items : [];
				this.expiringSoonCount = this.items.filter((i: any) => i.expiring_soon).length;
				if (this.flip === 'active') {
					this.viewData.emit({ data: this.items, title: 'Pending Auths' });
				}
				this.isLoading = false;
			});
	}

	voidAuth(a: any, ev: Event): void {
		ev.preventDefault();
		ev.stopPropagation();
		const key = this.rowKey(a);
		if (!key || this.actionBusy[key]) {
			return;
		}
		if (!confirm('Void this card authorization? The hold will be released at the processor.')) {
			return;
		}
		this.actionBusy[key] = true;
		this.voidFeedback = null;
		const payload = a.auth_id ? { auth_id: a.auth_id } : { transid: a.transid };
		this.customerService.voidPendingAuth(payload).subscribe({
			next: (r: any) => {
				this.actionBusy[key] = false;
				const msg = (r?.message && String(r.message).trim()) || (r?.success ? 'Authorization voided.' : 'Void failed.');
				if (r?.success) {
					this.voidFeedback = { type: 'success', text: msg };
					this.reloadList();
				} else {
					this.voidFeedback = { type: 'error', text: msg };
				}
				this.scheduleClearVoidFeedback();
			},
			error: (err: any) => {
				this.actionBusy[key] = false;
				const msg =
					err?.error?.message ||
					err?.error?.Message ||
					err?.message ||
					'Void request failed.';
				this.voidFeedback = { type: 'error', text: typeof msg === 'string' ? msg : 'Void request failed.' };
				this.scheduleClearVoidFeedback();
			},
		});
	}

	private scheduleClearVoidFeedback(): void {
		if (this.voidFeedbackClearTimer) {
			clearTimeout(this.voidFeedbackClearTimer);
		}
		this.voidFeedbackClearTimer = setTimeout(() => {
			this.voidFeedback = null;
			this.voidFeedbackClearTimer = null;
		}, 8000);
	}
}
